import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./UserManagement.css";
import { fetchAllUsers, createUser, deleteUser } from "../UserManagement/userManagementApiCalls";
import { clearTokens, isTokenExpired, storeLoginUrlPath, getTokenDetails } from '../../utils/authflow';

export default function UserManagement() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("usermailid");
    const [sortOrder, setSortOrder] = useState("asc");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [newType, setNewType] = useState("internal");
    const [creating, setCreating] = useState(false);
    const location = useLocation();
    const tokenDetails = getTokenDetails();

    useEffect(() => {
        fetchUsers();
        if (isTokenExpired()) {
            storeLoginUrlPath(location.pathname);
            clearTokens();
            navigate('/');
        }
    }, []);

    async function fetchUsers() {
        setLoading(true);
        setError("");
        try {
            const token = tokenDetails?.accessToken?.accessToken;
            const res = await fetchAllUsers(token);
            const data = await res.json();
            setUsers(data);
        } catch (err) {
            console.error(err);
            setError("Failed to load users");
        } finally {
            setLoading(false);
        }
    }


    async function handleDelete(user) {
        const token = tokenDetails?.accessToken?.accessToken;

        try {
            const data = await deleteUser(user.email, token);

            alert(data?.message ?? "User deleted successfully.");

            await fetchUsers(token);
        } catch (err) {
            console.error(err);
            alert(
                err?.message ??
                `Failed to delete user ${user.email}. Please try again.`
            );
        }
    }



    async function handleCreate(e) {
        e.preventDefault();
        setCreating(true);

        const token = tokenDetails?.accessToken?.accessToken;
        const userData = JSON.stringify({ email: newEmail, usertype: newType });

        try {
            const response = await createUser(userData, token); 
            const data = await response.json(); 

            if (response.ok) {
                alert(data?.message ?? "User created successfully.");

                setIsModalOpen(false);
                setNewEmail("");
                setNewType("internal");

                await fetchUsers(token);
            } else {
                alert(data?.message ?? "Failed to create user.");
            }
        } catch (err) {
            console.error(err);
            alert(err?.message ?? "Error creating user. Please try again.");
        } finally {
            setCreating(false);
        }
    }


    function toggleSort(column) {
        if (sortBy === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(column);
            setSortOrder("asc");
        }
    }

    const filteredUsers = useMemo(() => {
        return users.filter((u) =>
            u.email.toLowerCase().includes(search.toLowerCase())
        );
    }, [users, search]);

    const sortedUsers = useMemo(() => {
        const sorted = [...filteredUsers].sort((a, b) => {
            const valA = a[sortBy]?.toLowerCase?.() ?? "";
            const valB = b[sortBy]?.toLowerCase?.() ?? "";
            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
        return sorted.slice((page - 1) * limit, page * limit);
    }, [filteredUsers, sortBy, sortOrder, page, limit]);

    const totalPages = useMemo(() => Math.ceil(filteredUsers.length / limit), [filteredUsers.length, limit]);

    return (
        <div className="um-container">
            <header className="um-header">
                <h1>Manage Users</h1>
                <button className="primary-btn" onClick={() => setIsModalOpen(true)}>
                    Add New User
                </button>
            </header>

            <section className={isModalOpen ? "um-card blurred" : "um-card"}>
                <div className="um-toolbar">
                    <input
                        type="search"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                    <select
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setPage(1);
                        }}
                    >
                        {[5, 10, 20, 50].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                </div>

                <table className="um-table">
                    <thead>
                        <tr>
                            <th onClick={() => toggleSort("usermailid")}>Email</th>
                            <th onClick={() => toggleSort("usertype")}>User Type</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3}>Loading...</td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan={3}>{error}</td>
                            </tr>
                        ) : sortedUsers.length === 0 ? (
                            <tr>
                                <td colSpan={3}>No users found</td>
                            </tr>
                        ) : (
                            sortedUsers.map((u) => (
                                <tr key={u.id ?? u.email}>
                                    <td>{u.email}</td>
                                    <td>
                                        <span className="badge">{u.usertype}</span>
                                    </td>
                                    <td>
                                        <button className="danger-btn" onClick={() => handleDelete(u)}>
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <div className="pagination">
                    <button disabled={page === 1} onClick={() => setPage(1)}>First</button>
                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                    <span>Page {page} of {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last</button>
                </div>
            </section>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <form onSubmit={handleCreate}>
                            <h2>Add New User</h2>
                            <input
                                type="email"
                                placeholder="Email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                required
                            />
                            <select value={newType} onChange={(e) => setNewType(e.target.value)}>
                                <option value="internal">internal</option>
                                <option value="sysops">sysops</option>
                            </select>
                            <div className="modal-actions">
                                <button type="button" className="secondary-btn" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="primary-btn" disabled={creating}>
                                    {creating ? "Saving..." : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}